import type { PlacementStart } from './api/types.ts';
import { formatDate } from './dates.ts';

/**
 * What can be said about when the current placement began (Arthur, 2 October).
 * Two claims that must not be confused:
 *
 *  - "No move recorded since Allied was appointed" describes the RECORD. It is
 *    derived and provable, and needs no marker.
 *  - "Here since before Allied's involvement" describes the CLIENT. Somebody
 *    asserted it, so it says who and when. Its date is an effective one, never
 *    shown as if it were known.
 *
 * Otherwise a recorded date is a recorded date, and with nothing on record the
 * answer is nothing: no move-in date is ever invented.
 */
export type StartStatement =
  | { kind: 'asserted'; text: string }
  | { kind: 'no-move'; text: string }
  | { kind: 'dated'; since: string }
  | { kind: 'none' };

export function startStatement(ps: PlacementStart | null | undefined): StartStatement {
  if (!ps) return { kind: 'none' };
  if (ps.started_on_unknown) {
    const who = [ps.start_asserted_by, ps.start_asserted_on ? formatDate(ps.start_asserted_on) : null]
      .filter(Boolean)
      .join(', ');
    return { kind: 'asserted', text: `Here since before Allied's involvement${who ? ` · ${who}` : ''}` };
  }
  if (ps.started_on) return { kind: 'dated', since: ps.started_on };
  if (ps.appointed_on && !ps.moved_since) {
    return { kind: 'no-move', text: 'No move recorded since Allied was appointed' };
  }
  return { kind: 'none' };
}
